import { serialize } from "object-to-formdata";
import CryptoJS from 'crypto-js';

export const convertJsonToFormData = (data) => {
  const formData = serialize(data, { indices: true });

  return formData;
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const convertFormatedDate = (isoDateString, timeZone = 'Asia/Kolkata') => {
  if (!isoDateString) return '—';
  const date = new Date(isoDateString);
  if (isNaN(date.getTime())) return '—';

  // Use India timezone (IST) for consistent display
  try {
    const formatter = new Intl.DateTimeFormat('en-IN', {
      timeZone: timeZone || 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    const parts = formatter.formatToParts(date);
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    return `${get('day')} ${get('month')} ${get('hour')}:${get('minute')} ${get('dayPeriod')}`.trim();
  } catch {
    return date.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  }
};


export const encryptValue = (value) => {

  const secretKey = process.env.REACT_APP_HASHKEY;
  const encryptedValue = CryptoJS.AES.encrypt(value.toString(), secretKey).toString();
  return encryptedValue;
};

export const decryptValue = (encryptedValue) => {
  const secretKey = process.env.REACT_APP_HASHKEY;
  const bytes = CryptoJS.AES.decrypt(encryptedValue, secretKey);
  const decrypted = bytes.toString(CryptoJS.enc.Utf8);
  return decrypted;
};

export const formatTimeAgo = (timestamp) => {
  const currentTime = new Date();
  const givenTime = new Date(timestamp);
  const timeDifference = Math.floor((currentTime - givenTime) / 1000); 

  if (timeDifference < 60) {
    return `${timeDifference} seconds ago`;
  }

  const minutesDifference = Math.floor(timeDifference / 60);
  if (minutesDifference < 60) {
    return `${minutesDifference} minutes ago`;
  }

  const hoursDifference = Math.floor(minutesDifference / 60);
  if (hoursDifference < 24) {
    return `${hoursDifference} hours ago`;
  }

  const daysDifference = Math.floor(hoursDifference / 24);
  if (daysDifference < 7) {
    return `${daysDifference} days ago`;
  }

  const weeksDifference = Math.floor(daysDifference / 7);
  if (weeksDifference < 4) {
    return `${weeksDifference} weeks ago`;
  }

  const monthsDifference = Math.floor(daysDifference / 30); 
  if (monthsDifference < 12) {
    return `${monthsDifference} months ago`;
  }

  const yearsDifference = Math.floor(daysDifference / 365);
  return `${yearsDifference} years ago`;
};

export const calculateCompletionPercentage = (completedStream) => {
  
  const timeParts = completedStream.split(':').map(Number);

  let hours, minutes, seconds;

  if (timeParts.length === 2) {
    [minutes, seconds] = timeParts;
    hours = 0;
  } else if (timeParts.length === 3) {
    [hours, minutes, seconds] = timeParts;
  } else {
    return 0;
  }
  const totalSeconds = hours * 3600 + minutes * 60 + seconds;

  const totalDurationInSeconds = 3600;

  const percentage = (totalSeconds / totalDurationInSeconds) * 100;

  return percentage.toFixed(2); 
};


export const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs < 10 ? '0' : ''}${secs}`; 
};

export const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

export const base64ToBlob = (base64, mimeType) => {
  const base64Data = base64.split(',')[1] || base64;
  
  const byteCharacters = atob(base64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
    const slice = byteCharacters.slice(offset, offset + 1024);
    const byteNumbers = Array.from(slice, char => char.charCodeAt(0));
    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: mimeType });
};


export const urlToFile = async (imageUrl, fileName = 'image.jpg') => {
  const response = await fetch(imageUrl); 
  const blob = await response.blob();    
  const file = new File([blob], fileName, { type: blob.type }); 
  return file;
};


export const FullName = (firstName, lastName) => {
  return `${firstName?.charAt(0).toUpperCase()+firstName?.slice(1)} ${lastName?.charAt(0).toUpperCase()+lastName?.slice(1)}`;
};

export const titleCase = (value) => {
  if (value === null || value === undefined) return value;

  value = String(value);

  if (!value.length) return value;

  // Split by underscore if present
  const parts = value.split("_");

  // Title-case each part (first letter upper, rest same)
  const formatted = parts.map(part => {
    if (!part) return part;
    return part.charAt(0).toUpperCase() + part.slice(1);
  });

  // Join back with space
  return formatted.join(" ");
};


/**
 * Normalise a phone number to E.164 format.
 *
 * Rules (in priority order):
 *  1. If already starts with '+', keep it exactly as-is.
 *  2. If starts with '00', replace the leading 00 with '+'.
 *  3. If starts with '0' and remaining digits look like a national number
 *     (7-11 digits after the 0), use defaultCountryCode to build the full number.
 *  4. If no country prefix can be inferred, just prepend '+' to raw digits.
 *
 * defaultCountryCode is ONLY used as a last-resort fallback when the number
 * has no international prefix at all.  Pass '' to skip the fallback entirely
 * and return a bare digit string.
 */
export const toE164 = (num, defaultCountryCode = '') => {
  if (!num) return '';
  const trimmed = String(num).trim();

  // Already in E.164
  if (trimmed.startsWith('+')) return trimmed;

  // 00-prefixed international dialling
  if (trimmed.startsWith('00')) return '+' + trimmed.slice(2).replace(/\D/g, '');

  const digits = trimmed.replace(/\D/g, '');

  // National number with leading 0 (e.g. 07911123456 in the UK)
  if (digits.startsWith('0') && defaultCountryCode) {
    return '+' + defaultCountryCode + digits.slice(1);
  }

  // Bare digits with a known default country code
  if (defaultCountryCode) return '+' + defaultCountryCode + digits;

  // No country info — return with + so at least it's parseable
  return '+' + digits;
};

export const getImageUrl = (url = '') => {
  if (!url) return '';

  try {
    const absoluteProtocols = ['http:', 'https:', 'data:', 'blob:'];
    const parsed = new URL(url);

    if (absoluteProtocols.includes(parsed.protocol)) {
      return url;
    }
  } catch (err) {
  }
  const baseUrl = import.meta.env.VITE_API_URL;
  return new URL(url, baseUrl).href;
};

